import { TypedEmitter } from "@retreats/engine";
import {
  IExecutionPlane,
  RuntimeEvent,
  EXECUTION_PLANES,
  NODE_TYPES,
  UI_EVENTS,
  deriveDisplayName,
} from "@retreat/core";
import { createChildLogger } from "@retreat/shared";

/**
 * Text change event payload
 */
interface TextChangeEvent {
  text: string;
  offsetSeconds: number;
}

/**
 * WebTextPlane event map
 */
type WebTextPlaneEvents = {
  "text:change": (data: TextChangeEvent) => void;
};

/**
 * WebTextPlane: Bridge between @retreats/engine and React UI
 * Implements IExecutionPlane interface to integrate with RetreatManager
 *
 * Responsibilities:
 * - Filter primary plane events for text/instruction display
 * - Emit text:change events for React to consume
 * - Implement dirty-check to prevent unnecessary re-renders
 * - Handle seek/sync to clear stale text
 *
 * Event Flow:
 * RetreatManager  execute(RuntimeEvent)  emit('text:change')  React setState
 *
 * Performance:
 * - [P] Dirty check prevents 60fps re-renders if text unchanged
 * - Synchronous execute() doesn't block engine tick loop
 */
export class WebTextPlane
  extends TypedEmitter<WebTextPlaneEvents>
  implements IExecutionPlane
{
  private log = createChildLogger("WebTextPlane");
  private currentText: string = "";
  private preparedTextEvents: RuntimeEvent[] = [];

  constructor() {
    super();
    this.log.info("WebTextPlane created");
  }

  /**
   * IExecutionPlane: Execute event from engine
   *
   * CRITICAL: This is synchronous (returns void) to match IExecutionPlane contract.
   * Text updates are emitted via EventEmitter (React subscribes).
   *
   * Event Filtering:
   * 1. Check plane === "primary" (text instructions are on primary plane)
   * 2. Check payload.type === "silence" (silence nodes have contentKey text)
   * 3. Extract contentKey with default to empty string
   *
   * Performance: <1ms execution time. Dirty check prevents unnecessary emits.
   */
  execute(event: RuntimeEvent): void {
    // Guard: Only handle primary plane events (single source of truth)
    if (event.plane !== EXECUTION_PLANES.PRIMARY) {
      return;
    }

    // Guard: Validate payload exists
    if (!event.payload || typeof event.payload !== "object") {
      this.log.info("Event has invalid payload, skipping", {
        eventId: event.id,
      });
      return;
    }

    // Extract text from payload
    const { type, contentKey } = event.payload;

    // Only process silence nodes (they carry text instructions)
    if (type !== NODE_TYPES.SILENCE) {
      return;
    }

    const newText = contentKey ? deriveDisplayName(contentKey) : "";

    // [P]erformance: Dirty check - don't emit if text unchanged
    if (newText === this.currentText) {
      this.log.info(
        "Text unchanged (dirty check saved re-render): %s",
        this.currentText,
      );
      return;
    }

    this.log.info(
      'Text changed: "%s"  "%s" (event: %s, offset: %ds)',
      this.currentText,
      newText,
      event.id,
      event.offsetSeconds,
    );

    this.currentText = newText;

    // Emit to React (non-blocking)
    this.emit(UI_EVENTS.TEXT_CHANGE, {
      text: newText,
      offsetSeconds: event.offsetSeconds,
    } as TextChangeEvent);
  }

  /**
   * IExecutionPlane: Sync to a specific offset (for seek/pause/resume)
   *
   * Purpose: Reality check for text state when user jumps timeline.
   *
   * Example: User seeks from T=11s to T=3900s (Lunch).
   * - We must clear any text from T=11s ("ghost text")
   * - Reset state so next execute() at T=3900s works correctly
   *
   * RIGHT BICEP:
   * [B]oundary: What if seek lands exactly on a text event?
   * [I]nverse: sync() is inverse of execute(). Execute shows text, sync clears slate.
   *
   * @param offsetSeconds - New timeline position (optional, we don't need it for text)
   */
  /**
   * IExecutionPlane: Synchronize to a new timeline position
   *
   * @param offsetSeconds - New timeline position (optional, we don't need it for text)
   */
  sync(offsetSeconds?: number): void {
    this.log.info(
      "Syncing to offset %s, clearing current text",
      offsetSeconds !== undefined ? `${offsetSeconds}s` : "undefined",
    );

    // Clear current text to prepare for next event
    this.currentText = "";

    // Emit empty text to React (clear the display)
    this.emit(UI_EVENTS.TEXT_CHANGE, {
      text: "",
      offsetSeconds: offsetSeconds || 0,
    } as TextChangeEvent);

    this.log.info("Sync complete. Ready for next scheduled event.");
  }

  /**
   * IExecutionPlane: Stop all active updates (user hits Stop/Pause)
   *
   * Difference from sync():
   * - stopAll(): Total shutdown (user stops retreat)
   * - sync(): Timeline jump (user seeks, retreat still "on")
   */
  stopAll(): void {
    this.log.info("stopAll called, clearing text");
    this.currentText = "";
    this.emit(UI_EVENTS.TEXT_CHANGE, {
      text: "",
      offsetSeconds: 0,
    } as TextChangeEvent);
  }

  /**
   * IExecutionPlane: Prepare events (look-ahead / pre-warming hook)
   *
   * Called BEFORE timer starts (during initialize), not during runtime.
   *
   * Purpose in Offline-First PWA:
   * 1. Validation: Are all contentKeys valid strings?
   * 2. Performance: Filter text events ONCE here, not on every execute()
   * 3. Debug: Log expected text events for verification
   *
   * RIGHT BICEP Wins:
   * [P]erformance: Filter text events ONCE here, not on every execute()
   * [E]rror: Catch invalid contentKeys before retreat starts
   *
   * @param events - All events from the 1-day retreat DAG
   */
  async prepare(events: RuntimeEvent[]): Promise<void> {
    this.log.info("Preparing events from retreat DAG", {
      eventCount: events.length,
    });

    // Filter text events for validation (optimization)
    this.preparedTextEvents = events.filter(
      (e) =>
        e.plane === EXECUTION_PLANES.PRIMARY &&
        e.payload?.type === NODE_TYPES.SILENCE &&
        e.payload?.contentKey,
    );

    this.log.info(
      "Found %d text events in retreat",
      this.preparedTextEvents.length,
    );

    // Validation: Check for valid contentKeys
    const invalidTextEvents = this.preparedTextEvents.filter(
      (e) => typeof e.payload?.contentKey !== "string",
    );

    if (invalidTextEvents.length > 0) {
      const ids = invalidTextEvents.map((e) => e.id).join(", ");
      this.log.info("Invalid contentKeys detected", { ids });
      throw new Error(`Invalid contentKey types in events: ${ids}`);
    }

    // Debug: Log all prepared text events
    if (this.preparedTextEvents.length > 0) {
      this.log.info("Prepared text events:");
      this.preparedTextEvents.forEach((e) => {
        this.log.info(
          '  T=%ds: "%s" (id: %s)',
          e.offsetSeconds,
          e.payload?.contentKey,
          e.id,
        );
      });
    }

    this.log.info(
      "Preparation complete. Ready for %d text events",
      this.preparedTextEvents.length,
    );
  }

  /**
   * Get current text (for debugging/UI state)
   *
   * Usage:
   * - DevTools panel
   * - Debug overlay in development
   * - State persistence (save current instruction)
   */
  getCurrentText(): string {
    return this.currentText;
  }

  /**
   * Get prepared text events (for debugging)
   */
  getState() {
    return {
      currentText: this.currentText,
      preparedTextCount: this.preparedTextEvents.length,
    };
  }

  /**
   * Cleanup on unmount (prevent memory leaks)
   */
  dispose(): void {
    this.log.info("Disposing WebTextPlane");
    this.stopAll();
    this.removeAllListeners();
    this.log.info("WebTextPlane disposed");
  }
}
